import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {BehaviorSubject, firstValueFrom, from} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {FieldConfig as FaultFieldConfig} from '@docma-mm-core/field-config/field-config';
import {GetValue, SettingsService} from '../../../../mydocma-settings/settings.service';
import {SettingNamesEnum} from '../../../../mydocma-settings/setting-names.enum';
import {ProjectService} from '@mydocma/project';

@UntilDestroy()
@Injectable()
export class MmFaultFieldMemoryService {
  private memoryValuesLoadingSubject = new BehaviorSubject<boolean>(null)
  public memoryValuesLoading$ = this.memoryValuesLoadingSubject.asObservable();

  private currentMemoryValuesSubject = new BehaviorSubject<{[prop: string]: any}>(null);
  public currentMemoryValues$ = this.currentMemoryValuesSubject.asObservable();

  public get currentMemoryValues() {
    return this.currentMemoryValuesSubject.value;
  }

  constructor(private readonly projectService: ProjectService,
              private readonly settingsService: SettingsService) {
    this.projectService.onCurrentProjectChanged.pipe(untilDestroyed(this))
      .subscribe(project => {
        this.currentMemoryValuesSubject.next(null);
      })
  }

  async saveMemoryValues(changedMemoryValues: { [prop: string]: any }, fieldConfigsDict: { [prop: string]: FaultFieldConfig }) {
    if (changedMemoryValues && Object.keys(changedMemoryValues).length > 0) {
      const memoryValues = await this.getMemoryValues(fieldConfigsDict);


      for (const key of Object.keys(changedMemoryValues)) {
        if (changedMemoryValues.hasOwnProperty(key) && fieldConfigsDict.hasOwnProperty(key) && fieldConfigsDict[key].Memory) {
          memoryValues[key] = changedMemoryValues[key];
        } else {
          delete memoryValues[key];
        }
      }

      if (Object.keys(memoryValues)?.length === 0) {
        await firstValueFrom(this.projectService.currentProject
          .pipe(switchMap(project => this.settingsService.delete(PersonalizationKeysEnum.FaultFieldsMemoryValues, SettingNamesEnum.FaultFieldsMemoryValues, project)
          )));

        this.currentMemoryValuesSubject.next(null);
      } else {
        await firstValueFrom(this.projectService.currentProject
          .pipe(switchMap(project => this.settingsService.save(JSON.stringify(memoryValues), PersonalizationKeysEnum.FaultFieldsMemoryValues, SettingNamesEnum.FaultFieldsMemoryValues, project)
          )));

        this.currentMemoryValuesSubject.next(memoryValues);
      }
    }
  }

  async getMemoryValues(fieldConfigsDict: { [prop: string]: FaultFieldConfig }) {
    try {
      this.memoryValuesLoadingSubject.next(true)
      const memoryValues = await firstValueFrom(from(this.projectService.currentProject
        .pipe(tap(), switchMap(project => from(this.settingsService.load(PersonalizationKeysEnum.FaultFieldsMemoryValues, SettingNamesEnum.FaultFieldsMemoryValues, project))
          .pipe(map(async value => {
            const memoryValues: { [scope: string]: any } = {};
            const loaded = await GetValue<{ [scope: string]: any }>(value);

            if (loaded) {
              Object.keys(loaded).forEach(key => {
                if (fieldConfigsDict.hasOwnProperty(key) && fieldConfigsDict[key].Memory) {
                  memoryValues[key] = loaded[key];
                }
              });
            }
            return memoryValues;
          }))
        ))
      ));
      this.memoryValuesLoadingSubject.next(false)
      this.currentMemoryValuesSubject.next(memoryValues);
      return memoryValues;
    } catch (e) {
      this.memoryValuesLoadingSubject.next(false);
      console.error('an error occured while loading memory values')
    }


  }

}
